/* eslint-disable no-secrets/no-secrets */
import { ZenitClickAction } from '../models/events/click.model';
import { ZenitExperiment, ZenitExperiments } from '../models/events/page-hit-event.model';
import {
    Section,
    ZenitEventData,
    ZenitEventName,
    ZenitEventValues,
    ZenitItem,
    ZenitList,
    ZenitPageType,
    ZenitPersonalizationParam,
    ZenitZoomLevel,
} from '../models/zenit-general.model';

import { CartItem } from '@api/cart/cart.models';
import { GiftCard } from '@api/gift-card/models/gift-card.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { PageSection } from '@core/domain/types/page-sections.types';
import { ProductPublicModels } from '@features/products/product';
import { SidebarCustomComponents } from '@features/sidebar';
import { GridElement } from '@pages/category/modules/category-grid/public_api';
import { LIMIT_QUERY_SEARCH, QueryResult, SearchType } from '@pages/search';
import {
    OptimizelyCampaignsState,
    OptimizelyData,
    OptimizelyExperiment,
    OptimizelyState,
} from '@shared/ab-testing/models/optimizely.models';
import { EventFrom, ItemTypes } from '@shared/analytics/COMMON/models/analytics-common.models';
import * as AnalyticsUtils from '@shared/analytics/COMMON/services/analytics.utils';
import { eventFromParser } from '@shared/analytics/COMMON/services/analytics.utils';
import { ViewItemProduct } from '@shared/analytics/COMMON/services/product-view/view-item.service';
import { SearchRequestMethodName } from '@shared/analytics/store/analytics.state';
import { SearchRequestFilter } from '@shared/analytics/ZENIT/models/events/search-assistant.model';
import { GridType, GridTypes } from '@shared/models/grid-type.model';
import { ProductViewPlace, ProductViewPlaceValue } from '@shared/product/models';
import { ProductUid } from '@shared/product/related-products/domain/models/related-products.models';
import { RelatedsPlace } from '@shared/product/related-products/infrastructure/state/related-products.state';

// #region Modelos

interface OriginDataParams {
    place?: ProductViewPlace;
    searchRequestQuery?: string;
    searchType?: SearchType;
}

// #endregion

// #region Constantes

/**
 * EventFrom que pertenecen a orígenes de "relateds"
 */
const RelatedPlaces = [
    EventFrom.backSoon,
    EventFrom.cartYoda,
    EventFrom.comingSoon,
    EventFrom.completeLook,
    EventFrom.completeLookYoda,
    EventFrom.frecuentlyBoughtTogether,
    EventFrom.othersYouMayLike,
    EventFrom.recentlyViewed,
    EventFrom.recommended_for_you,
    EventFrom.relateds,
    EventFrom.relatedsBuyed,
    EventFrom.similar,
    EventFrom.similaresYoda,
    EventFrom.topSales,
    EventFrom.wishListRelated,
];

/**
 * Regex para identificar las páginas de soporte
 */
const HELP_PAGES_REGEX = /\/(help)(\/\w*)*/;

// #endregion

// #region Índice de item
/* ## Funciones para calcular el índice de un item en listas ## */

/**
 * Obtiene el índice de un item en una lista de resultados de búsqueda.
 * @param item Item objetivo
 * @param queryResults Resultados de búsqueda
 */
export const getQueryResultsIndex = <ItemType>(
    item: ItemType & { id?: number | string; productId?: string },
    queryResults: QueryResult[],
): number => {
    return queryResults?.find(product => product.id === item?.id)?.position;
};

/**
 * Función genérica para obtener el índice de un item en una lista, contemplando los casos con ID y productId.
 * @param item Item objetivo
 * @param itemList Lista de items en la que buscar
 */
export const getItemListIndex = <ItemType>(
    item: ItemType & { id?: number | string; productId?: string },
    itemList: Array<ItemType & { id?: number | string; productId?: string }> = [],
): number => {
    const itemId = typeof item?.id === 'number' ? item.id?.toString() : item?.id;
    const foundItem = itemList.find((_item) =>
        (_item?.id?.toString() || _item.productId) === (itemId || item?.productId));
    return itemList.indexOf(foundItem);
};

/**
 * Función para obtener un item de una lista de productos relacionados.
 * @param item Item objetivo
 * @param place Ubicación de los productos relacionados objetivo
 * @param relatedsSectionId Identificador de la sección objetivo en una ubicación de productos relacionados
 * @param relatedProductsPlaces Ubicaciones de relacionados
 */
export const getRelatedProduct = (
    item: { id?: number | string; productId?: string; moca?: string },
    place: ProductViewPlace,
    relatedsSectionId: string,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>,
): ProductUid => {
    const carousel = relatedProductsPlaces?.[place]
        ?.carousels?.[relatedsSectionId];
    return carousel?.filteredItemsIds.find((prodUid) => {
        return item.id === prodUid?.product_id || item.moca === prodUid?.uid?.slice(1);
    });
};

/**
 * Función para obtener el índice de un item de una lista de productos relacionados.
 * @param item Item objetivo
 * @param place Ubicación de los productos relacionados objetivo
 * @param relatedsSectionId Identificador de la sección objetivo en una ubicación de productos relacionados
 * @param relatedProductsPlaces Ubicaciones de relacionados
 */
export const getRelatedProductIndex = (
    item: { id?: number | string; productId?: string; moca?: string },
    place: ProductViewPlace,
    relatedsSectionId: string,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>,
): number => {
    const carousel = relatedProductsPlaces?.[place]
        ?.carousels?.[relatedsSectionId];
    return carousel?.filteredItemsIds.findIndex((prodUid) => {
        return item.id === prodUid?.product_id || item.moca === prodUid?.uid?.slice(1);
    });
};

/**
 * Función para obtener el índice de un item en todas las listas de items contempladas por la analítica de Zenit.
 * @param item Item objetivo
 * @param eventFrom Orígen del evento
 * @param values Datos para la generación del valor del index
 * @param eventData Información proporcionada en la invocación del evento para la generación de las propiedades
 */
export const getProductItemIndex = <ItemType>(
    item: ItemType & { id?: number | string; productId?: string },
    eventFrom: EventFrom,
    values: ZenitEventValues,
    eventData: ZenitEventData,
): number | null => {
    let index: number;
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (eventFrom) {
        case EventFrom.search:
        case EventFrom.searchResults:
            return getQueryResultsIndex(item, values.queryResults);

        case EventFrom.parrilla:
            index = AnalyticsUtils.getProductCategoryIndex(
                undefined,
                item as unknown as ProductPublicModels.PublicProduct,
                values.filteredProductsIds,
                values.productsArray,
            );
            break;

        case EventFrom.productdetail:
            return null;

        case EventFrom.shopcart:
            index = getItemListIndex<ItemType>(item, values.cartItems as unknown as ItemType[]);
            break;

        case EventFrom.wishList:
        case EventFrom.miniWishList:
            index = getItemListIndex<ItemType>(item, values.wishListItems as unknown as ItemType[]);
            break;

        case EventFrom.fichaBundle:
            index = getItemListIndex<ItemType>(item, values?.productPageProduct.bundleProducts as unknown as ItemType[]);
            break;

        case EventFrom.cartYoda:
        case EventFrom.completeLook:
        case EventFrom.completeLookYoda:
        case EventFrom.frecuentlyBoughtTogether:
        case EventFrom.othersYouMayLike:
        case EventFrom.recentlyViewed:
        case EventFrom.recommended_for_you:
        case EventFrom.relateds:
        case EventFrom.relatedsBuyed:
        case EventFrom.similar:
        case EventFrom.similaresYoda:
        case EventFrom.topSales:
        case EventFrom.wishListRelated:
            index = getRelatedProductIndex(
                item,
                eventData?.place,
                eventData?.relatedsSectionId,
                values?.relatedProductsPlaces,
            );
            break;

        default:
            index = item && values.items ? getItemListIndex<ItemType>(item, values.items as unknown as ItemType[]) : -1;
            break;
    }
    return index === -1 ? null : index + 1;
};

// #endregion

// #region pageType

/**
 * General el pageType para las páginas de CMS
 * @param currentUrl URL actual
 */
export const getCmsPagePageType = (currentUrl: string): ZenitPageType => {
    if (HELP_PAGES_REGEX.test(currentUrl)) {
        return ZenitPageType.HELP_CENTER;
    }
    return ZenitPageType.CATEGORY_VIEW;
};

/**
 * Genera el pageType para las páginas del buscador
 * @param eventData Información proporcionada en la invocación del evento para la generación de las propiedades
 * @param values Datos para la generación del valor del index
 * @param eventFrom Orígen del evento
 * @param eventName Nombre del evento objetivo
 */
export const getPageTypeForSearch = (
    eventData: ZenitEventData,
    values: ZenitEventValues,
    eventFrom: EventFrom,
    eventName: ZenitEventName,
): ZenitPageType => {
    if (eventName === ZenitEventName.SearchAssistant) return ZenitPageType.SEARCH_REQUEST;
    if (values?.searchRequestQuery === '') return ZenitPageType.PRODUCT_SEARCH_START;

    if ([EventFrom.search, EventFrom.searchResults].includes(eventFrom)
        || values?.searchRequestQuery !== '' && !eventData?.totalHits) {
        return ZenitPageType.PRODUCT_SEARCH_RESULTS;
    }
    return undefined;
};

/**
 * Función principal para generar el pageType de un evento, cuando sea posible utilizará el pageName,
 * si requiere lógica adiconal utilizará un helper.
 * @param values Datos para la generación del valor del index
 * @param eventData Información proporcionada en la invocación del evento para la generación de las propiedades
 * @param eventFrom Orígen del evento
 * @param eventName Nombre del evento objetivo
 */
export const getPageTypeByPage = (
    values: ZenitEventValues,
    eventData: ZenitEventData,
    eventFrom: EventFrom,
    eventName: ZenitEventName,
): ZenitPageType => {
    // Standard cases
    switch (values?.page) {
        case 'ItxAngularAuthenticationActivateFeelUserPage':
            return ZenitPageType.USER_LOYALTY_CHECK;
        case 'ItxAngularAuthenticationActivateUserPage':
            return ZenitPageType.USER_LOYALTY_TICKET;
        case 'ItxAngularAuthenticationConfirmationPage':
            return ZenitPageType.USER_REGISTRATION_CONFIRMATION;
        case 'ItxAngularAuthenticationPage':
            return ZenitPageType.USER_LOGON;
        case 'ItxAngularAuthenticationRecoveryPage':
            return values?.currentUrl?.includes('#resetPassword') ? ZenitPageType.PASSWORD_RECOVERY_RESET : ZenitPageType.PASSWORD_RECOVERY;
        case 'ItxAngularAuthenticationRegisterPage':
            return ZenitPageType.USER_REGISTRATION;
        case 'ItxAngularCmsRenderPage':
            return getCmsPagePageType(values?.currentUrl);
        case 'ItxAngularGiftCardActivationPage':
            return ZenitPageType.E_GIFTCARD_ACTIVATION;
        case 'ItxAngularGiftCardBalancePage':
            return ZenitPageType.E_GIFTCARD_BALANCE;
        case 'ItxAngularGiftCardPage':
            return ZenitPageType.GIFTCARD;
        case 'ItxAngularOrderConfirmationPage':
        case 'ItxAngularOrderConfirmationNodePage':
        case 'ItxOrderConfirmationPage':
            return ZenitPageType.PURCHASE_ORDER_END;
        case 'ItxAngularOrderStatus':
            return ZenitPageType.ORDER_TRACK;
        case 'ItxSearchPage':
            return getPageTypeForSearch(eventData, values, eventFrom, eventName);
        case 'ItxAngularShopcartPage':
            return values.cartItems?.length ? ZenitPageType.SHOPPING_CART : ZenitPageType.SHOPPING_CART_EMPTY;
        case 'ItxAngularStoreDetailLocatorPage':
            return ZenitPageType.STORE_DETAIL;
        case 'ItxAngularStoreLocatorPage':
            return ZenitPageType.SHOP_LOCATOR;
        case 'ItxAngularWishlistPage':
            return ZenitPageType.MULTI_WISHLIST;
        case 'ItxAngularLookbookPage':
            return ZenitPageType.CATEGORY_VIEW;
        case 'ItxCategoryPage':
            return ZenitPageType.PRODUCT_LIST;
        case 'ItxChatPage':
            return ZenitPageType.CHAT;
        case 'ItxCheckoutShippingPage':
            return ZenitPageType.CHECKOUT_SHIPPING_METHOD;
        case 'ItxCheckoutShippingDeliveryPage':
            return ZenitPageType.CHECKOUT_HOME_ADDRESS_LIST;
        case 'ItxCheckoutShippingDeliveryAddressPage':
            return values.isSelectedAddress
                ? ZenitPageType.CHECKOUT_MODIFY_PERSONAL_DETAILS : ZenitPageType.CHECKOUT_ADD_PERSONAL_DETAILS;
        case 'ItxCheckoutShippingDeliverySummaryPage':
            return ZenitPageType.CHECKOUT_TYPE_DELIVERY_METHOD;
        case 'ItxCheckoutShippingStoreSummaryPage':
            return ZenitPageType.CHECKOUT_STORE_DETAIL;
        case 'ItxCheckoutShippingDropPointSummaryPage':
            return ZenitPageType.CHECKOUT_DROP_POINT_DETAIL;
        case 'ItxCheckoutShippingStorePage':
            return values.selectedViewPickup === 'map' ? ZenitPageType.CHECKOUT_STORE_COLLECTION_POINT_MAP : ZenitPageType.CHECKOUT_STORE_LIST;
        case 'ItxCheckoutShippingDropPointPage':
            return values.selectedViewPickup === 'map' ? ZenitPageType.CHECKOUT_DROP_POINT_MAP : ZenitPageType.CHECKOUT_DROP_POINT_LIST;
        case 'ItxCheckoutPaymentPage':
            return ZenitPageType.CHECKOUT_PAYMENT_METHOD;
        case 'ItxCheckoutSummaryPage':
            return ZenitPageType.CHECKOUT_ORDER_REVIEW;
        case 'ItxCheckoutRepaymentPage':
            return ZenitPageType.CHECKOUT_REPAYMENT;
        case 'ItxAngularContactPage':
            return ZenitPageType.CONTACT;
        case 'ItxFastSintStoreListPage':
            return ZenitPageType.CHECKOUT_EXPRESS_STORE_LIST;
        case 'ItxFastSintStoreDetailPage':
            return ZenitPageType.CHECKOUT_EXPRESS_STORE_DETAIL;
        case 'ItxHomePage':
        case 'ItxHomeMenPage':
            return ZenitPageType.HOME;
        case 'ItxMassimoDuttiFeelPage':
            return ZenitPageType.HOME_LOYALTY;
        case 'ItxErrorPage':
        case 'Itx404ErrorPage':
        case 'Itx410ErrorPage':
            return ZenitPageType.PAGE_NOT_FOUND;
        case 'ItxMyAccountAccessDataPage':
            return ZenitPageType.MY_ACCOUNT_ACCESS_DETAILS;
        case 'ItxMyAccountAddressListPage':
            return ZenitPageType.MY_ACCOUNT_ADDRESSES;
        case 'ItxMyAccountAppointmentsPage':
            return ZenitPageType.MY_ACCOUNT_APPOINTMENTS;
        case 'ItxMyAccountCardsPage':
            return ZenitPageType.MY_ACCOUNT_WALLET;
        case 'ItxMyAccountDetailsPage':
            return ZenitPageType.MY_ACCOUNT_PERSONAL_DETAILS;
        case 'ItxMyAccountFeelBenefitsPage':
            return ZenitPageType.MY_ACCOUNT_LOYALTY_BENEFITS;
        case 'ItxMyAccountFeelHistoryPage':
            return ZenitPageType.MY_ACCOUNT_LOYALTY_HISTORY;
        case 'ItxMyAccountFeelHowItWorksPage':
            return ZenitPageType.MY_ACCOUNT_LOYALTY_HOW_IT_WORKS;
        case 'ItxMyAccountMenuPage':
            return ZenitPageType.MY_ACCOUNT_MENU;
        case 'ItxMyAccountNewsletterPage':
            return ZenitPageType.MY_ACCOUNT_NEWSLETTER;
        case 'ItxMyAccountOrderExchanges':
            return ZenitPageType.MY_ACCOUNT_ORDER_RETURNS_EXCHANGES_ITEMS;
        case 'ItxMyAccountOrderReturnableProducts':
            return ZenitPageType.RETURNABLE_PRODUCTS_SELECTOR;
        case 'ItxMyAccountOrdersOnline':
            return ZenitPageType.MY_ACCOUNT_ORDERS_LIST_ONLINE;
        case 'ItxMyAccountOrdersStore':
            return ZenitPageType.MY_ACCOUNT_ORDERS_LIST_IN_STORE;
        case 'ItxMyAccountReturnItemsPage':
            return ZenitPageType.RETURNABLE_PRODUCTS_SELECTOR;
        case 'ItxMyAccountReturnsPage':
            return ZenitPageType.MY_ACCOUNT_RETURNS_LIST;
        case 'ItxNewsletterPage':
            return ZenitPageType.NEWSLETTER_SUBSCRIBE_VIEW;
        case 'ItxNewsletterUnsuscribePage':
            return ZenitPageType.NEWSLETTER_UNSUBSCRIBE_VIEW;
        case 'ItxProductPage':
            return values.productPageProduct?.type === 'BundleBean'
                ? ZenitPageType.PRODUCT_BUNDLE_DETAILS
                : ZenitPageType.PRODUCT_DETAILS;
        case 'ItxStoriesPage':
            return ZenitPageType.CATEGORY_VIEW;
        case 'ItxWorldWidePage':
            return ZenitPageType.WORLDWIDE;
        default:
            return ZenitPageType.UNKNOWN;
    }
};

/**
 * Función que genera el pageType de un evento a partir de las condiciones especiales o la función principal.
 * @param values Datos para la generación del valor del index
 * @param eventFrom Orígen del evento
 * @param eventData Información proporcionada en la invocación del evento para la generación de las propiedades
 * @param eventName Nombre del evento objetivo
 */
export const getPageType = (
    values: ZenitEventValues,
    eventFrom: EventFrom,
    eventData: ZenitEventData,
    eventName: ZenitEventName,
): ZenitPageType => {
    // Special cases
    if (eventFrom === EventFrom['shop-cart-packagin']) {
        return ZenitPageType.GIFT_OPTIONS;
    }
    if (eventData?.place === ProductViewPlaceValue.CS_BS) return ZenitPageType.COMING_SOON_CONFIRMATION;

    // Page type by page and values
    return getPageTypeByPage(values, eventData, eventFrom, eventName);
};

// #endregion

/**
 * Obtiene la sección definida en el estado de la aplicación, si no está definida se obtiene del localStorage
 * @param section State current section
 * @param commonWindow Window object reference
 */
export const getSection = (section: string, commonWindow?: CommonWindow): Section => {
    if (section) return Section[section] || Section.UNKNOWN;

    const lastSection = commonWindow.localStorage.getItem('LAST_SECTION');
    return Section[lastSection] || Section.UNKNOWN;
};

/**
 * Obtiene el identificador de una categoría parseada a number o -1 si no está definido
 * @param page
 * @param categoryId
 * @param categoryProductId
 * @returns number
 */
export const getCategoryId = (categoryId: string): number => {
    return categoryId ? +categoryId : -1; // Added + for number format
};

/**
 * Obtiene la sección de una URL de categoría necesaria para la analítica de Zenit
 * @param categoryUrl URL de categoría completa
 */
export const getCategoryURL = (categoryUrl: string): string => {
    const categoryUrlSplit = categoryUrl ? categoryUrl.split('-') : undefined;
    return categoryUrlSplit ? categoryUrlSplit[categoryUrlSplit.length - 1] : null;
};

/**
 * Obtiene el catentryId a partir de todos los tipos de item contemplados para la analítica de Zenit
 * @param item Item de orígen
 */
export const getCatentryId = (item: ItemTypes): number => {
    if (!item) {
        return null;
    }

    if ('catentryId' in item) return item.catentryId;

    // Caso ficha bundle
    if ((item as ProductPublicModels.PublicProduct)?.type === 'BundleBean') {
        return Number.parseInt((item as ProductPublicModels.PublicProduct).id);
    }

    if ('status' in item && item.status?.selectedColor?.catentryId) {
        return item.status.selectedColor.catentryId;
    }

    return null;
};

/**
 * Obtiene el partNumber que corresponde a la talla seleccionada del item
 * @param item Item de orígen
 */
export const getPartNumber = (
    item: ItemTypes | GiftCard,
): string | null => {
    return (
        (item as ProductPublicModels.PublicProduct)?.status || (item as CartItem)
    )?.selectedSize?.partnumber || (item as GiftCard)?.partnumber
        || (item as CartItem)?.reference || null;
};

/**
 * Obtiene la referencia completa de un item en el formato definido para Zenit
 * @param item Item de orígen
 */
export const getReferenceStyle = (item: ItemTypes | GiftCard): string | -1 => {
    const itemStatus = (item as ProductPublicModels.PublicProduct)?.status;

    if (itemStatus) return itemStatus?.selectedColor?.reference;
    if ((item as CartItem)?.reference) {
        const referenceSplit = (item as CartItem).reference.split('-');
        const mocacoStyle = referenceSplit[0]?.substring(0, 11);

        if (mocacoStyle) {
            return `C${mocacoStyle}-${referenceSplit[1]}`;
        }
    }

    return (item as GiftCard)?.partnumber || -1;
};

/**
 * Genera un listado de skus a partir de una lista de items
 * @param items Items de orígen
 */
export const getSkus = (
    items: ItemTypes[] | GiftCard[],
): string[] => {
    return items.map((item) => getPartNumber(item));
};

/**
 * Devuelve el valor del cartId si es distinto de -1, en caso contrario devuelve null
 * @param cartId cartId disponible
 */
export const getCartId = (cartId: number | '-1'): number => {
    return cartId === '-1' ? null : cartId;
};

/**
 * Devuelve el valor del storeId parseado a number si está disponible, en caso contrario devuelve null
 * @param storeId Identificador de la tienda actual
 */
export const getStoreId = (storeId: string): number => {
    return storeId ? Number.parseInt(storeId) : null;
};

/**
 * Obtiene el valor del currencyCode de la currencyInfo, que coincide con el formato establecido para Zenit
 * @param currency Información de currency del mercado actual
 */
export const getCurrency = (currency: CurrencyInfo): string => {
    return currency?.currencyCode;
};

/**
 * Genera un listado de objetos de items de Zenit a partir de una lista de items del shopcart
 * @param items Items del shopcart
 */
export const getItems = (items: CartItem[]): ZenitItem[] => {
    return items?.map((item: CartItem): ZenitItem => {
        return {
            amount: item?.unitPrice * item?.quantity,
            articleBrandCode: 11,
            brandId: 11,
            price: item?.unitPrice,
            section: getSection(item?.sectionNameEN),
            sku: getSkus([item])?.[0],
            units: item?.quantity,
            viewOrigin: null, // No tenemos este dato por ahora
        };
    });
};

/**
 * Mapea el tipo de grid seleccionado en una categoría o el buscador a los equivalentes de Zenit
 * @param gridType Tipo de grid seleccionado
 */
export const getZoomLevel = (gridType: GridType): ZenitZoomLevel => {
    return {
        1: ZenitZoomLevel.ZOOM1,
        2: ZenitZoomLevel.ZOOM2,
        3: ZenitZoomLevel.ZOOM3,
    }[gridType];
};

/**
 * Genera un objeto de personalización de Zenit, que hace referencia a los orígenes de los productos relacionados
 * @param item Item objetivo
 * @param relatedProductsPlaces Ubicaciones de relacionados
 * @param place Ubicación de los productos relacionados objetivo
 * @param relatedsSectionId Identificador de la sección objetivo en una ubicación de productos relacionados
 */
export const getPersonalization = (
    item: ItemTypes,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>,
    place: ProductViewPlace,
    relatedsSectionId: string,
): ZenitPersonalizationParam => {
    const relatedsData = getRelatedProduct(item, place, relatedsSectionId, relatedProductsPlaces);
    return {
        objective: relatedsData?.modelName,
        provider: relatedsData?.provider,
        strategy: relatedsData?.relatedType,
        version: relatedsData?.modelVersion,
    };
};

/**
 * Obtiene la cantidad de un item, priorizando la cantidad especificada en el evento
 * @param eventQuantity Cantidad especificada en el evento
 * @param item Item del cual obtener una cantidad
 */
export const getQuantity = (eventQuantity: number, item: ItemTypes): number => {
    if (eventQuantity) return eventQuantity;
    if ('quantity' in item) return item.quantity;
    return undefined;
};

// #region Listas

/**
 * Geneara la lista de un evento de búsqueda, contemplando los diferentes tipos de búsqueda y orígenes.
 * @param originData Datos complementarios del orígen del evento
 * @param eventFrom Orígen del evento
 */
export const getListForSearch = (originData: OriginDataParams, eventFrom: EventFrom): ZenitList => {
    if (RelatedPlaces.includes(eventFrom)
        && originData?.searchRequestQuery) return ZenitList.searchAlternativeResults;
    if (RelatedPlaces.includes(eventFrom)) return ZenitList.searchCrossCustom;

    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (originData?.searchType) {
        case 'busqueda_relacionada':
        case 'busqueda_sugerida':
        case 'trending':
        case 'ultimas_busquedas':
            return ZenitList.suggestedProduct;
        default:
            return ZenitList.searchResults;
    }
};

/**
 * Genera la lista de orígen de un evento desde productos relacionados.
 * @param page Página actual
 * @param eventFromParsed Orígen del evento
 * @param sidebarPlace Orígen de sidebar
 * @param place Ubicación de productos relacionados
 */
export const getListForRelateds = (
    page: PageSection,
    eventFromParsed: EventFrom,
    sidebarPlace: EventFrom,
    place: Partial<ProductViewPlace>,
): ZenitList => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (place) {
        case ProductViewPlaceValue.ADD_CART:
            return ZenitList.crossCustomToast;

        case ProductViewPlaceValue.CART:
            return ZenitList.crossSellingCart;

        case ProductViewPlaceValue.CS_BS:
            return {
                [EventFrom.backSoon]: ZenitList.crossSimilarBackSoon,
                [EventFrom.comingSoon]: ZenitList.crossSimilarComingSoon,
            }[sidebarPlace];

        case ProductViewPlaceValue.OUT_OF_STOCK: {
            const listParser = {
                [EventFrom.productdetail]: ZenitList.crossSimilarPdp,
                ItxCategoryPage: ZenitList.crossSimilarGrid,
                ItxProductPage: ZenitList.crossSimilarCustomPdp,
            };
            return listParser[sidebarPlace] || listParser[page] || ZenitList.noListUndefined;
        }

        case ProductViewPlaceValue.PRODUCT_GRID:
            return ZenitList.crossSimilarGrid;
    }

    const crossSelling: Record<PageSection, ZenitList> = {
        ItxProductPage: ZenitList.crossCustomPdp,
    };

    return {
        [EventFrom.backSoon]: ZenitList.crossSimilarBackSoon,
        [EventFrom.comingSoon]: ZenitList.crossSimilarComingSoon,
        [EventFrom.completeLook]: crossSelling[page],
        [EventFrom.completeLookYoda]: crossSelling[page],
        [EventFrom.frecuentlyBoughtTogether]: crossSelling[page],
        [EventFrom.othersYouMayLike]: ZenitList.mayAlsoLike,
        [EventFrom.recentlyViewed]: ZenitList.crossViewedRecently,
        [EventFrom.recommended_for_you]: crossSelling[page],
        [EventFrom.relateds]: crossSelling[page],
        [EventFrom.relatedsBuyed]: crossSelling[page],
        [EventFrom.similar]: crossSelling[page],
        [EventFrom.topSales]: crossSelling[page],
        [EventFrom.wishListRelated]: crossSelling[page],
    }[eventFromParsed] || ZenitList.noListUndefined;
};

/**
 * Genera la lista de orígen del evento a partir del eventFrom de orígen.
 * @param eventFrom Orígen del evento
 */
export const getListForPage = (
    eventFrom: EventFrom,
): ZenitList => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (eventFrom) {
        case 'parrilla':
        case 'giftcard':
            return ZenitList.grid;

        case 'ficha_bundle':
        case 'vista_rapida_bundle':
            return ZenitList.bundle;

        case 'miniWishList':
        case 'wishList':
            return ZenitList.wishlist;

        case 'shopcart':
        case 'miniCart':
            return ZenitList.shoppingCart;

        case 'directo':
            return ZenitList.noListUndefined;

        case 'search':
        case 'searchResults':
            return ZenitList.searchResults;

        case 'productdetail':
            return null;

        default:
            return ZenitList.noListUndefined;
    }
};

/**
 * Genera la lista de orígen del evento según las condiciones para el buscador, relacionados y el resto de orígenes eventFrom.
 * @param eventFrom Orígen del evento
 * @param page Página actual
 * @param item Item objetivo del evento
 * @param originData Datos complementarios del orígen del evento
 * @param sidebarData Datos del sidebar actualmente abierto
 */
export const getList = (
    eventFrom: EventFrom,
    page: PageSection,
    item: ItemTypes,
    originData: OriginDataParams,
    sidebarData: { name: SidebarCustomComponents; data: any },
): ZenitList => {
    const { eventFromParsed, sidebarPlace } = eventFromParser(
        eventFrom,
        item,
        sidebarData,
    );

    if (originData?.place === ProductViewPlaceValue.SEARCH) return getListForSearch(originData, eventFromParsed);
    if (RelatedPlaces.includes(eventFromParsed)) {
        return getListForRelateds(page, eventFromParsed, sidebarPlace, originData?.place);
    }

    return getListForPage(eventFrom);
};

// #endregion

// #region Experiments
// ############### EXPERIMENTS PROPERTY LOGIC ###############

/**
 * Obtiene el identificador de una campaña de optimizely a partir del identificador de un experimento.
 * @param campaigns Listado de campañas de optimizely activas
 * @param experimentId Identificador del experimento objetivo
 */
export const getOptimizelyExperimentCampaignId = (campaigns: OptimizelyCampaignsState, experimentId: string): string => {
    return Object.values(campaigns).find((campaing) => {
        return campaing.experiment.id === experimentId;
    })?.id;
};

/**
 * Genera un objeto de experimento de Zenit a partir de un experimento de Optimizely.
 * @param experiment Información del experimento objetivo
 * @param data Información general de optimizely
 * @param campaigns Listado de campañas de optimizely activas
 * @param experimentId Identificador del experimento objetivo
 */
export const formOptimizelyExperimentObject = (
    experiment: OptimizelyExperiment,
    data: OptimizelyData,
    campaigns: OptimizelyCampaignsState,
    experimentId: string,
): ZenitExperiment => {
    const optimizelyExperimentCampaignId = getOptimizelyExperimentCampaignId(campaigns, experimentId);

    return {
        experimentDetails: {
            experimentAccountId: Number.parseInt(data.accountId),
            experimentCampaignId: Number.parseInt(optimizelyExperimentCampaignId),
            experimentId: Number.parseInt(experiment.id),
            experimentProjectId: Number.parseInt(data.projectId),
        },
        experimentKey: experiment.experimentName,
        experimentName: experiment.experimentName,
        provider: 'optimizely',
        variationKey: experiment.variation.name,
    };
};

/**
 * Obtiene la lista de experimentos activos de Optimizely desde el objeto "optimizely" del window.
 * @param commonWindow Referencia al objeto window
 */
export const getOptimizelyExperiments = (commonWindow: CommonWindow): ZenitExperiment[] => {
    if (!commonWindow?.optimizely?.initialized) {
        return [];
    }

    const data = commonWindow.optimizely.get('data') as OptimizelyData;
    const state = commonWindow.optimizely.get('state') as OptimizelyState;
    const campaigns = state.getCampaignStates({ isActive: true });
    const stateExperiments = state.getExperimentStates({ isActive: true });

    return Object.entries(stateExperiments).reduce((acc: ZenitExperiment[], [experimentId, val]) => {
        acc.push(formOptimizelyExperimentObject(val, data, campaigns, experimentId));
        return acc;
    }, []);
};

/**
 * Genera el objeto de experimentos de Zenit a partir de los experimentos de todos los proveedores.
 * @param commonWindow Referencia al objeto window
 */
export const getExperiments = (commonWindow: CommonWindow): ZenitExperiments => {
    /** Si en el futuro se incluyen más proveedores se añadirán aquí */
    const optimizely = getOptimizelyExperiments(commonWindow);

    return { optimizely };
};

// #endregion

/**
 * Comprueba si la talla del producto seleccionada es la misma que la talla recomendada.
 * * Si no hay definida una recomendación de talla se devuelve null.
 * * Si el producto no contiene un objeto de status se devuelve null.
 * * Si el evento no es AddToCart o Click con action de tipo "select-size" se devuelve null.
 * * Si las condiciones previas se cumplen se devuelve el resultado de la comparación entre la talla del producto y la talla recomendada.
 * @param eventName Nombre del evento
 * @param eventAction Acción del evento
 * @param product Item objetivo
 * @param recommendedSize Talla recomendada guardada
 */
export const getIsRecommended = (
    eventName: ZenitEventName,
    eventAction: ZenitClickAction,
    product: ItemTypes,
    recommendedSize: string,
): string | null => {
    if (!recommendedSize) return null;
    if (!('status' in product)) return null;
    if (
        eventName !== ZenitEventName.AddToCart
        && !(eventName === ZenitEventName.Click && eventAction === ZenitClickAction['select-size'])
    ) return null;

    return (product?.status?.selectedSize?.name === recommendedSize).toString();
};

/**
 * En caso de que el evento sea de tipo AddToCart o Click con action de tipo "select-size" y se haya guardado una talla recomendada
 * se devuelve el valor de "sastre", en caso contrario se devuelve null.
 * @param eventName Nombre del evento
 * @param eventAction Acción del evento
 * @param recommendedSize Talla recomendada guardada
 */
export const getRecommender = (eventName: ZenitEventName, eventAction: ZenitClickAction, recommendedSize: string) => {
    if (
        eventName !== ZenitEventName.AddToCart
        && !(eventName === ZenitEventName.Click && eventAction === ZenitClickAction['select-size'])
    ) return null;

    return recommendedSize ? 'sastre' : null;
};

// #region Search request

/**
 * Devuelve el valor de los filtros activos en la funcionalidad de búsqueda
 * @param _filters String de filtros activos, concatenados con comas
 */
export const getSearchRequestFilters = (_filters: string): SearchRequestFilter[] => {
    if (!_filters) {
        return [];
    }

    const filters: Record<string, string[]> = _filters.split(',').reduce((acc, curr) => {
        const [_key, value] = curr.split(':');
        const key = _key.trim();

        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(value);
        return acc;
    }, {});

    return Object.entries(filters).map(([name, values]) => ({
        name: name.split('_')?.[0].toUpperCase(),
        values,
        numElements: values.length,
    }));
};

/**
 * Devuelve el valor del offset definido para las peticiones de búsqueda
 * @param searchRequestOffset Offset de la búsqueda
 */
export const getSearchRequestOffset = (searchRequestOffset = 0): number => {
    if (searchRequestOffset === 0) {
        return 0;
    }

    return searchRequestOffset - LIMIT_QUERY_SEARCH;
};

/**
 * Función principal para los parámetros específicos de eventos de búsqueda. Genera el valor
 * de cada parámetro a partir de los datos de la búsqueda y los valores del evento.
 * Los casos que tienen lógica adicional se manejan con helpers.
 * @param param Parámetro a generar
 * @param values Datos para la generación del valor del index
 * @param eventData Información proporcionada en la invocación del evento para la generación de las propiedades
 */
export const getSearchRequestParams = (
    param: string,
    values: ZenitEventValues,
    eventData: ZenitEventData,
): number | string | SearchRequestFilter[] | null => {
    switch (param) {
        case 'searchRequestResponseTime':
            return eventData.responseTime ?? null;
        case 'searchRequestResultsNum': {
            const resultsNum = Number.parseInt(eventData?.totalHits?.toString());
            return Number.isNaN(resultsNum) ? null : resultsNum;
        }
        case 'searchRequestMethodName':
            return SearchRequestMethodName.query;
        case 'searchRequestLimit':
            return LIMIT_QUERY_SEARCH;
        case 'searchRequestHTTPResponseCode':
            return eventData.responseCode ?? -1;
        case 'searchRequestOffset':
            return getSearchRequestOffset(values.searchRequestOffset);
        case 'searchRequestProvider':
            return eventData.provider;
        case 'searchRequestPass':
            return eventData.searchPass;
        case 'searchRequestFilters':
            return getSearchRequestFilters(values.searchRequestFilters);
    }
};

// #endregion

/**
 * Obtiene el valor de una propiedad de extraInfo de un item proporcionado.
 * @param item Item del cual extraer el valor
 * @param property Propiedad de extraInfo a extraer
 */
export const getItemMediaExtraInfoProperty = (item: ViewItemProduct | ItemTypes, property: string): string => {
    const idMedia = (item as ViewItemProduct)?.idMedia;
    if (!idMedia) return null;

    try {
        for (const medias of item?.xmedia as any[]) {
            for (const xmediaItem of medias?.xmediaItems as any[]) {
                const foundPropertyValue = xmediaItem?.medias?.find((media) => {
                    return media?.idMedia === idMedia;
                })?.extraInfo?.[property];

                if (foundPropertyValue) {
                    return foundPropertyValue;
                }
            }
        }
        return null;
    } catch {
        return null;
    }
};

/**
 * Mapea el tipo de grid al templateType equivalente de Zenit. En caso del tipo de grid "STUDIO" se obtendrá
 * el templateType a partir del template utilizado para el item proporcionado.
 * @param targetItem Item objetivo
 * @param gridType Configuración de vista de grid actual
 * @param gridElements Listado de elementos en el grid
 */
export const getTemplateType = (
    targetItem: ItemTypes,
    gridType: GridType,
    gridElements: GridElement[],
): string => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (gridType) {
        case GridTypes.RELATEDS:
        case GridTypes.SEARCH:
            return null;

        case GridTypes.ZARA_STUDIO: {
            const productId = typeof targetItem?.id === 'string' ? Number.parseInt(targetItem.id) : targetItem?.id;
            return gridElements?.find((gridElement) => gridElement?.ccIds?.includes(productId))?.template || null;
        }

        case GridTypes.GRID_4:
            return 'GRID_TYPE_2';

        case GridTypes.GRID_16:
            return 'GRID_TYPE_3';
    }
};
